/**
 * Exposes window.openQuickAdd() for external widgets (e.g. chatbot)
 * to programmatically open the quick-add modal rendered in layout/theme.liquid
 * via snippets/quick-add-modal.liquid (id "QuickAdd-external").
 *
 * Usage:
 *   window.openQuickAdd({
 *     productHandle: 'my-product',        // required — Shopify product handle
 *     variantId: '44012345678',           // required — the variant to pre-select
 *     properties: { '_source': 'widget' } // optional — line-item properties for the cart
 *   });
 *
 * Returns a Promise that resolves once the modal has been opened, or rejects on
 * invalid input (e.g. missing product) or if opening fails.
 */
(function () {
  var MODAL_ID = 'QuickAdd-external';

  /**
   * Injects hidden <input> fields for line-item properties into the product form.
   */
  function injectProperties(modalContent, properties) {
    if (!properties || typeof properties !== 'object') return;

    var form = modalContent.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    Object.entries(properties).forEach(function (entry) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'items[0][properties][' + entry[0] + ']';
      input.value = String(entry[1]);
      form.appendChild(input);
    });
  }

  /**
   * Re-injects stored line-item properties after a variant change replaces the
   * modal HTML. Listens to the `quick-add:template-updated` event dispatched by
   * quick-add.js (extension point) instead of duplicating updateTemplate().
   */
  function bindPropertyReinjection(modal) {
    if (modal._propertyReinjectionBound) return;
    modal._propertyReinjectionBound = true;

    modal.addEventListener('quick-add:template-updated', function () {
      if (modal._externalProperties) {
        injectProperties(modal.modalContent, modal._externalProperties);
      }
    });
  }

  /**
   * Opens the quick-add modal for a given product.
   *
   * @param {Object} options
   * @param {string} options.productHandle - The product URL handle (slug)
   * @param {string} options.variantId     - The variant to pre-select
   * @param {Object} [options.properties]  - Key/value pairs for cart line-item properties
   * @returns {Promise<void>}
   */
  window.openQuickAdd = function (options) {
    options = options || {};
    var productHandle = options.productHandle;
    var variantId = options.variantId;
    var properties = options.properties;

    if (!productHandle) return Promise.reject(new Error('openQuickAdd: productHandle is required'));
    if (!variantId) return Promise.reject(new Error('openQuickAdd: variantId is required'));

    var modal = document.getElementById(MODAL_ID);
    if (!modal) return Promise.reject(new Error('openQuickAdd: modal element #' + MODAL_ID + ' not found in DOM'));
    if (modal._externalOpenPending || modal.hasAttribute('open')) {
      return Promise.reject(new Error('openQuickAdd: modal is already opening or open'));
    }
    modal._externalOpenPending = true;

    var routesRoot = (((window.Shopify || {}).routes || {}).root || '/').replace(/\/+$/, '') + '/';
    var productUrl = routesRoot + 'products/' + encodeURIComponent(productHandle)
      + '?section_id=quick-add&call_from_modal=true'
      + '&formId=external-quick-add-' + Date.now()
      + '&variant=' + encodeURIComponent(variantId);

    // Store properties so they persist across variant changes.
    modal._externalProperties = properties || null;
    if (properties) bindPropertyReinjection(modal);

    // Create a fake opener that satisfies QuickAddModal.show().
    var fakeOpener = document.createElement('button');
    fakeOpener.setAttribute('data-product-url', productUrl);
    fakeOpener.style.display = 'none';
    fakeOpener.innerHTML = '<span class="loading__spinner hidden"></span>';
    document.body.appendChild(fakeOpener);

    var observer = new MutationObserver(function () {
      if (!modal.hasAttribute('open')) return;
      observer.disconnect();
      if (modal._externalProperties) {
        injectProperties(modal.modalContent, modal._externalProperties);
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    // Single cleanup path — safe to call from success, close, or failure.
    var cleanedUp = false;
    function cleanup() {
      if (cleanedUp) return;
      cleanedUp = true;
      observer.disconnect();
      document.body.removeEventListener('modalClosed', onModalClosed);
      modal._externalProperties = null;
      modal._externalOpenPending = false;
      fakeOpener.remove();
    }

    // Named handler + manual removeEventListener: `{ once: true }` would be
    // consumed by the first modalClosed of ANY modal, leaking our opener/observer.
    function onModalClosed(e) {
      if (e.detail && e.detail.id !== MODAL_ID) return;
      cleanup();
    }
    document.body.addEventListener('modalClosed', onModalClosed);

    // modal.show() kicks off an async fetch; if it throws synchronously or the
    // returned promise rejects, clean up so nothing leaks. Reset the pending flag
    // on success once the modal is actually open.
    return Promise.resolve()
      .then(function () {
        return modal.show(fakeOpener);
      })
      .then(function () {
        modal._externalOpenPending = false;
      })
      .catch(function (error) {
        cleanup();
        throw error;
      });
  };
})();
